import sort from '@bybeam/platform-lib/utilities/sort';
import { Payment, PaymentPattern } from '@bybeam/platform-types';
import { Column } from '@platform-ui-common/components/Table/types.js';
import useFulfillment from '@platform-ui-common/hooks/contexts/useFulfillment.js';
import { DateFormat, formatCurrency, formatDate } from '@platform-ui-common/utilities/format.js';
import {
  PaymentMethodDisplay,
  PaymentScheduleDisplay,
  getApplicantIndividualRecurringPaymentStatus,
  hasValidScheduledPayments,
} from '@platform-ui-common/utilities/payment.js';
import dayjs from 'dayjs';
import PanelWrapper from '../../CaseDetails/components/PanelWrapper.js';
import { Box, Card, DataList, Flex, Table, Text } from '@radix-ui/themes';
import styles from './RecurringPaymentOverview.module.css';

interface TableRow {
  id?: string;
  scheduledFor: string;
  status: string;
  transactionId: string;
  amount: string;
  method: string;
}

const COLUMNS: Column<TableRow>[] = [
  {
    Header: 'Date',
    accessor: 'scheduledFor',
  },
  {
    Header: 'Payment Status',
    accessor: 'status',
  },
  {
    Header: 'Amount',
    accessor: 'amount',
  },
  {
    Header: 'Payment Method',
    accessor: 'method',
  },
];

function prepData(payments: Payment[], paymentPattern: PaymentPattern): TableRow[] {
  if (!hasValidScheduledPayments(payments)) return [];
  return sort(payments, {
    accessor: ({ scheduledFor }) => dayjs(scheduledFor),
    ascending: true,
  }).map((payment, idx) => {
    return {
      id: payment.id,
      scheduledFor: payment.scheduledFor
        ? formatDate(payment.scheduledFor, DateFormat.ShortSlashes, true)
        : '',
      status: getApplicantIndividualRecurringPaymentStatus(payment, idx, paymentPattern.count ?? 0),
      transactionId: payment.initiatedAt ? payment.displayId : '-',
      amount: formatCurrency(payment.amount ?? 0, true),
      method: `${PaymentMethodDisplay[payment.method]} - ${
        PaymentScheduleDisplay[paymentPattern?.pattern]
      }`,
    };
  });
}

export default function RecurringPaymentOverview(): JSX.Element {
  const { paymentPattern, payments } = useFulfillment();
  const rows = prepData(payments, paymentPattern);

  return (
    <PanelWrapper title="Payment Overview">
      {rows.length > 0 ? (
        <>
          <div className={styles.desktopView}>
            <Table.Root variant="surface">
              <Table.Header>
                <Table.Row>
                  {COLUMNS.map((column) => (
                    <Table.ColumnHeaderCell key={column.accessor}>
                      {column.Header as string}
                    </Table.ColumnHeaderCell>
                  ))}
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {rows.map((row) => (
                  <Table.Row key={row.id}>
                    {COLUMNS.map(({ accessor }) => (
                      <Table.Cell key={accessor}>{row[accessor]}</Table.Cell>
                    ))}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </div>
          <div className={styles.mobileView}>
            <Flex direction="column" gap="2">
              {rows.map((row) => (
                <Card key={row.id} variant="surface">
                  <DataList.Root>
                    {COLUMNS.map(({ Cell, accessor, Header }) => (
                      <DataList.Item key={Header as string} align="start">
                        <DataList.Label minWidth="130px">{Header as string}</DataList.Label>
                        <DataList.Value>{row[accessor]}</DataList.Value>
                      </DataList.Item>
                    ))}
                  </DataList.Root>
                </Card>
              ))}
            </Flex>
          </div>
        </>
      ) : (
        <Box pt="5" pb="4">
          <Text>No Payments.</Text>
        </Box>
      )}
    </PanelWrapper>
  );
}
